/**
 * Export utilities for downloading data as CSV
 */

import Papa from 'papaparse';

export function exportToCSV(data: object[], filename: string): void {
  const csv = Papa.unparse(Array.isArray(data) ? data : []);
  const safeFilename = filename.toLowerCase().endsWith('.csv') ? filename : `${filename}.csv`;

  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.setAttribute('href', url);
  link.setAttribute('download', safeFilename);
  link.style.visibility = 'hidden';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export interface OverviewExportData {
  fiscalWeek: number;
  totalRevenue: number;
  totalUnits: number;
  totalSKUs: number;
  totalStores: number;
  retailers: Array<{
    name: string;
    revenue: number | null;
    units: number | null;
    skus: number | null;
    stores: number | null;
  }>;
}

/**
 * Export Overview Dashboard data to CSV
 */
export function exportOverviewToCSV(data: OverviewExportData) {
  const year = Math.floor(data.fiscalWeek / 100);
  const week = data.fiscalWeek % 100;
  
  // Build CSV rows
  const rows: any[] = [
    // Header row
    ['PatternIQ Overview Dashboard'],
    [`Week ${week}, ${year} (Fiscal Week ${data.fiscalWeek})`],
    [], // Empty row
    
    // Column headers
    ['Metric', 'Total', ...data.retailers.map(r => r.name)],
    
    // Data rows
    [
      'Total Revenue ($)',
      data.totalRevenue,
      ...data.retailers.map(r => r.revenue !== null ? r.revenue : 'N/A')
    ],
    [
      'Units Sold',
      data.totalUnits,
      ...data.retailers.map(r => r.units !== null ? r.units : 'N/A')
    ],
    [
      'Active SKUs',
      data.totalSKUs,
      ...data.retailers.map(r => r.skus !== null ? r.skus : 'N/A')
    ],
    [
      'Total Stores',
      data.totalStores,
      ...data.retailers.map(r => r.stores !== null ? r.stores : 'N/A')
    ],
    
    [], // Empty row
    ['Generated by PatternIQ', new Date().toISOString()],
  ];
  
  // Convert to CSV
  const csv = Papa.unparse(rows);
  
  // Create blob and download
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  
  link.setAttribute('href', url);
  link.setAttribute('download', `patterniq-overview-week-${data.fiscalWeek}-${Date.now()}.csv`);
  link.style.visibility = 'hidden';
  
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  
  URL.revokeObjectURL(url);
}

/**
 * Format number for export (handles null values)
 */
export function formatExportNumber(value: number | null | undefined): string | number {
  if (value === null || value === undefined) return 'N/A';
  return value;
}

/**
 * Format currency for export (handles null values)
 */
export function formatExportCurrency(value: number | null | undefined): string {
  if (value === null || value === undefined) return 'N/A';
  return `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
